import type { TwilioEnvVar, TwilioHealth } from '@/api/endpoints/platform';

/**
 * The health check at the top of TwilioConfig (W6, DUP-5): each line says
 * what is wrong, why, and what to do, from the facts of
 * GET /api/admin/platform/twilio-health. Pure, so the rules are tested apart
 * from the screen. Texts are i18n keys under admin:twilioConfig.health.
 */
export type CheckStatus = 'ok' | 'warn' | 'error' | 'skipped';

export interface HealthCheck {
  id: 'credentials' | 'account' | 'deviceToken' | 'twimlApp' | 'voiceUrl';
  status: CheckStatus;
  /** i18n key suffix under admin:twilioConfig.health.checks.<id>. */
  state: string;
  params?: Record<string, string | number>;
  /** The URL to copy when the voice URL is wrong. */
  copy?: string;
}

const list = (vars: TwilioEnvVar[]) => vars.join(', ');

/** The voice URL Twilio holds equals the expected one, a trailing slash aside. */
export function sameUrl(a: string, b: string): boolean {
  return a.trim().replace(/\/+$/, '').toLowerCase() === b.trim().replace(/\/+$/, '').toLowerCase();
}

export function twilioHealthChecks(h: TwilioHealth): HealthCheck[] {
  const missing = (vars: TwilioEnvVar[]) => vars.filter((v) => !h.credentials[v]);
  const checks: HealthCheck[] = [];

  const accountVars = missing(['TWILIO_ACCOUNT_SID', 'TWILIO_AUTH_TOKEN']);
  checks.push(accountVars.length
    ? { id: 'credentials', status: 'error', state: 'missing', params: { vars: list(accountVars) } }
    : { id: 'credentials', status: 'ok', state: 'ok' });

  if (!h.account.checked) {
    checks.push({ id: 'account', status: 'skipped', state: 'skipped' });
  } else if (!h.account.reachable) {
    const refused = h.account.http_status === 401 || h.account.http_status === 403;
    checks.push({
      id: 'account',
      status: 'error',
      state: refused ? 'refused' : h.account.http_status === null ? 'network' : 'failed',
      params: { status: h.account.http_status ?? '—', error: h.account.error },
    });
  } else if (h.account.status !== 'active') {
    checks.push({ id: 'account', status: 'error', state: 'inactive', params: { status: h.account.status } });
  } else {
    checks.push({ id: 'account', status: 'ok', state: 'ok', params: { name: h.account.friendly_name } });
  }

  const tokenVars = missing(['TWILIO_API_KEY', 'TWILIO_API_SECRET']);
  checks.push(tokenVars.length
    ? { id: 'deviceToken', status: 'error', state: 'missing', params: { vars: list(tokenVars) } }
    : { id: 'deviceToken', status: 'ok', state: 'ok' });

  if (!h.credentials.TWILIO_TWIML_APP_SID) {
    checks.push({ id: 'twimlApp', status: 'error', state: 'missing' });
    checks.push({ id: 'voiceUrl', status: 'skipped', state: 'skipped' });
  } else if (!h.twiml_app.checked) {
    checks.push({ id: 'twimlApp', status: 'skipped', state: 'skipped' });
    checks.push({ id: 'voiceUrl', status: 'skipped', state: 'skipped' });
  } else if (!h.twiml_app.found) {
    checks.push({ id: 'twimlApp', status: 'error', state: 'notFound', params: { status: h.twiml_app.http_status ?? '—', error: h.twiml_app.error } });
    checks.push({ id: 'voiceUrl', status: 'skipped', state: 'skipped' });
  } else {
    checks.push({ id: 'twimlApp', status: 'ok', state: 'ok', params: { name: h.twiml_app.friendly_name || h.twiml_app.sid } });
    const app = h.twiml_app;
    if (!app.voice_url) {
      checks.push({ id: 'voiceUrl', status: 'error', state: 'empty', params: { expected: h.expected_voice_url }, copy: h.expected_voice_url });
    } else if (!sameUrl(app.voice_url, h.expected_voice_url)) {
      checks.push({ id: 'voiceUrl', status: 'error', state: 'mismatch', params: { current: app.voice_url, expected: h.expected_voice_url }, copy: h.expected_voice_url });
    } else if (app.voice_method && app.voice_method.toUpperCase() !== 'POST') {
      checks.push({ id: 'voiceUrl', status: 'warn', state: 'method', params: { method: app.voice_method } });
    } else {
      checks.push({ id: 'voiceUrl', status: 'ok', state: 'ok' });
    }
  }
  return checks;
}

/** Blocking problems first, for the summary line. */
export function healthSummary(checks: HealthCheck[]): { errors: number; warnings: number } {
  return {
    errors: checks.filter((c) => c.status === 'error').length,
    warnings: checks.filter((c) => c.status === 'warn').length,
  };
}
